import { useState } from 'react'
import { fmtPhone } from '@/entities/client'
import {
  type Booking,
  type Excursion,
  useCancelBookingMutation,
  usePlaceManualBookingMutation,
} from '@/entities/excursion'
import { useSession } from '@/entities/session'
import { errorMessage } from '@/shared/api'
import { cn, pluralRu } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Chip } from '@/shared/ui/chip'
import { Dialog } from '@/shared/ui/dialog'
import { Field, Input, Textarea } from '@/shared/ui/field'
import { Icon } from '@/shared/ui/icon'
import { toast } from '@/shared/ui/toast'

interface BookingListProps {
  excursion: Excursion
}

/**
 * Записи выхода: кто идёт, сколько мест, откуда пришли. Клиент отменить себя
 * не может — на публичной странице нет входа и нечем доказать личность,
 * поэтому отмена всегда через команду.
 *
 * Гид список видит (ему вести этих людей), но не правит: записями распоряжается
 * администратор.
 */
export function BookingList({ excursion }: BookingListProps) {
  const place = usePlaceManualBookingMutation()
  const cancel = useCancelBookingMutation()
  const [addOpen, setAddOpen] = useState(false)
  const [cancelling, setCancelling] = useState<Booking | null>(null)
  const [reason, setReason] = useState('')
  const [form, setForm] = useState({ name: '', phone: '', seats: '1', note: '' })

  const { session } = useSession()
  const bookings = excursion.bookings ?? []
  const planned = excursion.state === 'planned' && session?.isAdmin === true

  const submitAdd = (event: React.FormEvent) => {
    event.preventDefault()
    place.mutate(
      {
        excursionId: excursion.id,
        name: form.name.trim(),
        phone: form.phone.trim(),
        seats: Number(form.seats) || 1,
        note: form.note.trim(),
      },
      {
        onSuccess: () => {
          setAddOpen(false)
          setForm({ name: '', phone: '', seats: '1', note: '' })
          toast.success('Записали')
        },
      },
    )
  }

  const submitCancel = (event: React.FormEvent) => {
    event.preventDefault()
    if (!cancelling) return
    cancel.mutate(
      { excursionId: excursion.id, bookingId: cancelling.id, reason: reason.trim() },
      {
        onSuccess: () => {
          setCancelling(null)
          setReason('')
        },
      },
    )
  }

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h2 className="label">Записи</h2>
        {planned && (
          <Button size="sm" variant="secondary" onClick={() => setAddOpen(true)}>
            Записать
          </Button>
        )}
      </div>

      {bookings.length === 0 ? (
        <p className="text-sm text-text-tertiary">Пока никто не записался.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-border-subtle">
          {bookings.map((booking) => (
            <li key={booking.id} className="flex items-center gap-3 py-2.5">
              <span className="flex min-w-0 flex-1 flex-col">
                <span
                  className={cn(
                    'truncate text-sm',
                    booking.active ? 'text-text-primary' : 'text-text-tertiary line-through',
                  )}
                >
                  {booking.clientName}
                </span>
                <span className="mono-num flex items-center gap-1 text-xs text-text-tertiary">
                  <Icon name="phone" size={12} />
                  {fmtPhone(booking.clientPhone)}
                </span>
                {booking.note && <span className="text-xs text-text-tertiary">{booking.note}</span>}
              </span>
              {/* Голое число рядом с чипом читалось как код, а не как места. */}
              <span className="shrink-0 text-right text-xs text-text-secondary sm:w-20">
                <span className="mono-num text-sm">{booking.seats}</span>{' '}
                {pluralRu(booking.seats, 'место', 'места', 'мест')}
              </span>
              <span className="hidden shrink-0 sm:block">
                <Chip variant="soft">{booking.source === 'public' ? 'по ссылке' : 'вручную'}</Chip>
              </span>
              {planned && booking.active ? (
                <Button variant="ghost" size="sm" onClick={() => setCancelling(booking)}>
                  Снять
                </Button>
              ) : (
                // Пустой слот держит колонку: иначе «Снять» у соседних строк
                // разъезжается по горизонтали.
                <span aria-hidden className="hidden w-[4.75rem] shrink-0 sm:block" />
              )}
            </li>
          ))}
        </ul>
      )}

      <Dialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="Записать вручную"
        footer={
          <>
            <Button variant="ghost" onClick={() => setAddOpen(false)}>
              Отмена
            </Button>
            <Button type="submit" form="manual-booking" disabled={place.isPending}>
              Записать
            </Button>
          </>
        }
      >
        <form id="manual-booking" onSubmit={submitAdd} className="flex flex-col gap-4">
          <Field label="Имя">
            {(props) => (
              <Input
                {...props}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                autoFocus
              />
            )}
          </Field>
          <Field label="Телефон" hint="Один номер может быть у нескольких человек — это нормально">
            {(props) => (
              <Input
                {...props}
                type="tel"
                inputMode="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            )}
          </Field>
          <Field label="Сколько мест">
            {(props) => (
              <Input
                {...props}
                type="number"
                min={1}
                value={form.seats}
                onChange={(e) => setForm({ ...form, seats: e.target.value })}
              />
            )}
          </Field>
          <Field label="Что просили передать">
            {(props) => (
              <Textarea
                {...props}
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
              />
            )}
          </Field>
          {place.isError && (
            <p role="alert" className="text-sm text-accent">
              {errorMessage(place.error)}
            </p>
          )}
        </form>
      </Dialog>

      <Dialog
        open={cancelling !== null}
        onClose={() => setCancelling(null)}
        title="Снять запись"
        footer={
          <>
            <Button variant="ghost" onClick={() => setCancelling(null)}>
              Не снимать
            </Button>
            <Button
              type="submit"
              form="cancel-booking"
              variant="danger"
              disabled={cancel.isPending || reason.trim().length === 0}
            >
              Снять
            </Button>
          </>
        }
      >
        <form id="cancel-booking" onSubmit={submitCancel} className="flex flex-col gap-4">
          <Field label="Причина" hint="Передумал / не пришёл / сняли сами">
            {(props) => (
              <Input
                {...props}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                autoFocus
              />
            )}
          </Field>
          {cancel.isError && (
            <p role="alert" className="text-sm text-accent">
              {errorMessage(cancel.error)}
            </p>
          )}
        </form>
      </Dialog>
    </section>
  )
}
